import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { RefreshCw, Search } from 'lucide-react'
import { AdminBookingConfirmDialog, type AdminDestructiveAction } from '../admin/admin-booking-confirm-dialog'
import { AdminBookingDetail } from '../admin/admin-booking-detail'
import { AdminBookingMenu } from '../admin/admin-booking-menu'
import { AdminSidebar } from '../admin/admin-sidebar'
import { MetaGalleryPanel } from '../admin/meta-gallery-panel'
import { SquareConnectionPanel } from '../admin/square-connection-panel'
import { XeroConnectionPanel } from '../admin/xero-connection-panel'
import {
  isDestructiveBookingAction,
  toAdminBookingSearch,
  type AdminBookingAction,
} from '../../lib/admin-booking-actions'
import {
  ADMIN_BOOKING_SELECT,
  asAdminBookings,
  bookingCustomerName,
  bookingListStatus,
  bookingPackage,
  isTestBooking,
  matchesBookingFilter,
  matchesBookingQuery,
  type AdminBookingFilter,
  type AdminCanonicalBooking,
} from '../../lib/admin-workspace'
import { supabase } from '../../lib/supabase'
import type { PublicSquareConnection } from '../../lib/square-oauth'
import type { PublicXeroConnection } from '../../lib/xero-oauth'
import { PageTitle } from '../seo/page-title'

const filters: Array<{ id: AdminBookingFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'awaiting_payment', label: 'Awaiting Payment' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'test', label: 'TEST' },
]

export function AdminBookings({
  bookingReference,
  action,
}: Readonly<{
  bookingReference?: string
  action?: 'edit' | 'cancel' | 'delete'
}>) {
  const [bookings, setBookings] = useState<AdminCanonicalBooking[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editing, setEditing] = useState(action === 'edit')
  const [filter, setFilter] = useState<AdminBookingFilter>('all')
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [message, setMessage] = useState<string | null>(null)
  const [messageError, setMessageError] = useState(false)
  const [xeroConnection, setXeroConnection] = useState<PublicXeroConnection | null>(null)
  const [squareConnection, setSquareConnection] = useState<PublicSquareConnection | null>(null)
  const [pendingAction, setPendingAction] = useState<AdminDestructiveAction | null>(
    isDestructiveBookingAction(action) ? action : null,
  )
  const [confirmBooking, setConfirmBooking] = useState<AdminCanonicalBooking | null>(null)
  const allowAutoSelect = useRef(!bookingReference)
  const navigate = useNavigate({ from: '/admin/bookings' })

  const loadBookings = useCallback(async (silent = false) => {
    if (!silent) setStatus('loading')
    const { data, error } = await supabase
      .from('bookings')
      .select(ADMIN_BOOKING_SELECT)
      .order('created_at', { ascending: false })
      .limit(300)

    if (error) {
      setMessage(error.message)
      setMessageError(true)
      setStatus('error')
      return
    }

    setBookings(asAdminBookings(data))
    setStatus('ready')
  }, [])

  useEffect(() => {
    void loadBookings()
  }, [loadBookings])

  const filtered = useMemo(
    () => bookings.filter((booking) => matchesBookingFilter(booking, filter) && matchesBookingQuery(booking, query)),
    [bookings, filter, query],
  )

  useEffect(() => {
    if (bookingReference) {
      const match = bookings.find((booking) => booking.public_reference === bookingReference)
      if (match) setSelectedId(match.id)
      return
    }
    if (!allowAutoSelect.current) return
    setSelectedId((current) => current ?? filtered[0]?.id ?? bookings[0]?.id ?? null)
  }, [bookingReference, bookings, filtered])

  useEffect(() => {
    if (action === 'edit') setEditing(true)
  }, [action, bookingReference])

  useEffect(() => {
    if (!isDestructiveBookingAction(action) || !bookingReference) return
    const match = bookings.find((booking) => booking.public_reference === bookingReference)
    if (!match) return
    setConfirmBooking(match)
    setPendingAction(action)
    void navigate({
      to: '/admin/bookings',
      search: { booking: bookingReference, action: undefined },
      replace: true,
    })
  }, [action, bookingReference, bookings, navigate])

  const selectedBooking = bookings.find((booking) => booking.id === selectedId)
    ?? filtered.find((booking) => booking.id === selectedId)
    ?? null

  function report(next: string | null, error = false) {
    setMessage(next)
    setMessageError(error)
  }

  function showBooking(booking: AdminCanonicalBooking, nextAction?: Extract<AdminBookingAction, 'view' | 'edit'>) {
    setSelectedId(booking.id)
    setEditing(nextAction === 'edit')
    void navigate({
      to: '/admin/bookings',
      search: toAdminBookingSearch(booking.public_reference, nextAction),
      replace: true,
    })
  }

  function viewBooking(booking: AdminCanonicalBooking) {
    showBooking(booking, 'view')
  }

  function editBooking(booking: AdminCanonicalBooking) {
    showBooking(booking, 'edit')
  }

  function cancelBooking(booking: AdminCanonicalBooking) {
    setConfirmBooking(booking)
    setPendingAction('cancel')
  }

  function deleteTestBooking(booking: AdminCanonicalBooking) {
    setConfirmBooking(booking)
    setPendingAction('delete')
  }

  function handleMenuAction(booking: AdminCanonicalBooking, menuAction: AdminBookingAction) {
    if (menuAction === 'edit') editBooking(booking)
    else if (menuAction === 'cancel') cancelBooking(booking)
    else if (menuAction === 'delete') deleteTestBooking(booking)
    else viewBooking(booking)
  }

  return (
    <div className="admin-dashboard">
      <PageTitle title="Bookings | AUTOGLOW Detailing Admin" />
      <AdminSidebar active="bookings" />
      <main>
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Bookings</p>
            <h1>Ceramic Bookings</h1>
          </div>
          <button type="button" onClick={() => void loadBookings()} disabled={status === 'loading'}>
            <RefreshCw size={17} /> Refresh
          </button>
        </header>

        {message ? <p className={messageError ? 'admin-alert error' : 'admin-alert'}>{message}</p> : null}

        <XeroConnectionPanel onConnectionChange={setXeroConnection} />
        <SquareConnectionPanel onConnectionChange={setSquareConnection} />
        <MetaGalleryPanel />

        <div className="admin-filter-bar">
          {filters.map((item) => (
            <button
              className={filter === item.id ? 'active' : undefined}
              key={item.id}
              onClick={() => setFilter(item.id)}
              type="button"
            >
              {item.label}
            </button>
          ))}
          <label className="admin-search-field">
            <Search size={16} />
            <input
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search reference, customer, email, phone or vehicle"
              value={query}
            />
          </label>
        </div>

        <section className="admin-record-grid">
          <article className="admin-card admin-record-list">
            <h2>Bookings</h2>
            {status !== 'loading' && filtered.length === 0 ? <p className="admin-empty-state">No ceramic bookings match that filter.</p> : null}
            {filtered.map((booking) => (
              <div className={selectedBooking?.id === booking.id ? 'admin-booking-list-row selected' : 'admin-booking-list-row'} key={booking.id}>
                <button type="button" onClick={() => viewBooking(booking)}>
                  <strong>{booking.public_reference}{isTestBooking(booking) ? ' TEST' : ''}</strong>
                  <span>{bookingCustomerName(booking)} - {bookingPackage(booking).displayName}</span>
                  <small>{bookingListStatus(booking)} / deposit {booking.payment_status}</small>
                </button>
                <AdminBookingMenu
                  booking={booking}
                  includeDelete={isTestBooking(booking)}
                  onAction={handleMenuAction}
                />
              </div>
            ))}
          </article>

          <article className="admin-card admin-record-detail">
            {selectedBooking ? (
              <AdminBookingDetail
                key={selectedBooking.id}
                booking={selectedBooking}
                editing={editing}
                onEditingChange={setEditing}
                onChanged={() => loadBookings(true)}
                onRequestCancel={() => cancelBooking(selectedBooking)}
                onMessage={report}
                xeroConnection={xeroConnection}
                squareConnection={squareConnection}
              />
            ) : <p className="admin-empty-state">Select a booking.</p>}
          </article>
        </section>
      </main>
      <AdminBookingConfirmDialog
        action={pendingAction}
        booking={confirmBooking}
        onCancelled={() => { void loadBookings(true) }}
        onDeleted={(bookingId) => {
          allowAutoSelect.current = false
          setSelectedId((current) => current === bookingId ? null : current)
          setEditing(false)
          void navigate({
            to: '/admin/bookings',
            search: { booking: undefined, action: undefined },
            replace: true,
          })
          void loadBookings(true)
        }}
        onDismiss={() => {
          setPendingAction(null)
          setConfirmBooking(null)
        }}
        onMessage={report}
      />
    </div>
  )
}

export default AdminBookings
